import axios from 'axios';
import { notify, notifyKey } from './components/CustomToast';
import CryptoJS from 'crypto-js';
import { translateApiMessage } from './i18n';

// Configuration
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8648/api';
// In a real app, this should be an environment variable.
const ACCESS_KEY_SECRET = process.env.REACT_APP_ACCESS_KEY || 'secret_frontend_key_123';
const LOCALE_STORAGE_KEY = 'unit_c_locale';
const DEFAULT_LOCALE = 'en';
const SUPPORTED_LOCALES = new Set(['en', 'ur', 'ar']);

// Create axios instance
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    withCredentials: true,
});

// Request Interceptor: Access Key Handshake (HMAC)
api.interceptors.request.use((config) => {
    const requestedLocale = localStorage.getItem(LOCALE_STORAGE_KEY) || DEFAULT_LOCALE;
    const locale = SUPPORTED_LOCALES.has(requestedLocale) ? requestedLocale : DEFAULT_LOCALE;
    const timestamp = Math.floor(Date.now() / 1000).toString();
    const method = config.method.toUpperCase();

    // Ensure relative path starts with / and remove duplicate slashes
    let path = config.url.replace(config.baseURL, '');
    if (!path.startsWith('/')) path = '/' + path;

    // Remove query string for signature calculation as backend uses $request->path()
    const pathWithoutQuery = path.split('?')[0];
    const fullPath = '/api' + (pathWithoutQuery.startsWith('/') ? pathWithoutQuery : '/' + pathWithoutQuery);

    // Sanitize request data to prevent circular reference errors
    if (config.data) {
        try {
            // Verify data is JSON serializable
            JSON.stringify(config.data);
        } catch (e) {
            console.error('Request data contains circular references or non-serializable values:', config.data);
            const sanitized = {};
            for (const key in config.data) {
                if (config.data.hasOwnProperty(key)) {
                    const val = config.data[key];
                    if (val !== null && typeof val === 'object' && typeof val.toJSON !== 'function') {
                        sanitized[key] = String(val);
                    } else {
                        sanitized[key] = val;
                    }
                }
            }
            config.data = sanitized;
        }
    }

    const signatureData = timestamp + method + fullPath;
    const signature = CryptoJS.HmacSHA256(signatureData, ACCESS_KEY_SECRET).toString();

    config.headers['X-Timestamp'] = timestamp;
    config.headers['X-Signature'] = signature;
    config.headers['Accept-Language'] = locale;
    config.headers['X-Locale'] = locale;

    // Attach Bearer token if exists
    const token = localStorage.getItem('token');
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
}, (error) => {
    return Promise.reject(error);
});

// Response Interceptor: Error Handling
api.interceptors.response.use(
    (response) => response.data, // Return direct data
    (error) => {
        const { response } = error;
        if (error.config?.skipErrorToast && (!response || response.status >= 500)) {
            return Promise.reject(error);
        }

        if (response) {
            // 422: Validation Error - Pass through to component to handle specific fields
            if (response.status === 422) {
                const validationError = {
                    status: 422,
                    message: translateApiMessage(response.data.message, 'api.validation_failed'),
                    errors: response.data.data || {}
                };
                return Promise.reject(validationError); // Component catches this
            }

            // 401: Unauthorized
            if (response.status === 401) {
                if (window.location.pathname.includes('/login')) {
                    // On login page: pass message through so component can display it
                    return Promise.reject({
                        status: 401,
                        message: response.data.message || '',
                        errors: {}
                    });
                }
                // Elsewhere: session expired, redirect
                localStorage.removeItem('token');
                window.location.href = '/login';
                notifyKey('error', 'api.session_expired_title', 'api.session_expired_message');
            } else if (response.status === 403) {
                notifyKey('error', 'api.security_error_title', 'api.request_rejected');
            } else {
                // 500 or others
                notify('error', translateApiMessage(response.data.message, 'api.generic_error'));
            }
        } else {
            // Silently reject requests canceled via AbortController (no toast)
            if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
                return Promise.reject(error);
            }

            // Check for serialization/circular reference errors
            if (error.message && error.message.includes('circular structure')) {
                console.error('Circular reference in request data:', error);
                notify('error', 'Invalid data format - please refresh and try again');
                return Promise.reject({
                    status: 400,
                    message: 'Invalid data format',
                    errors: {}
                });
            }

            notifyKey('error', 'api.network_error_title', 'api.network_error_message');
        }

        return Promise.reject(error);
    }
);

export default api;
